import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import FileUploader from "@/components/FileUploader";
import { Plus, Pencil, Trash2, Upload as UploadIcon, Copy } from "lucide-react";
import { useRef } from "react";

const EMPTY = { name: "", category: "", description: "", price: 0, price_on_request: false, images: [""], hsn: "", gst_rate: 18, brochure_url: "", specs: {}, is_active: true };
const REFURB_EXTRA = { condition: "Excellent", warranty_months: 6, availability: true };

export default function AdminCatalog({ resource, isRefurb = false }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ ...EMPTY, ...(isRefurb ? REFURB_EXTRA : {}) });
  const [specText, setSpecText] = useState("");
  const [importing, setImporting] = useState(false);
  const csvRef = useRef();

  const load = () => api.get(`/${resource}?active_only=false`).then(r => setItems(r.data));
  useEffect(() => { load(); }, [resource]);

  const doCsvImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/${resource}/bulk-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} rows`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setImporting(false); if (csvRef.current) csvRef.current.value = ""; }
  };

  const openNew = () => {
    setEdit(null);
    setForm({ ...EMPTY, ...(isRefurb ? REFURB_EXTRA : {}) });
    setSpecText("");
    setOpen(true);
  };
  const openEdit = (p) => {
    setEdit(p);
    setForm({ ...EMPTY, ...(isRefurb ? REFURB_EXTRA : {}), ...p });
    setSpecText(Object.entries(p.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n"));
    setOpen(true);
  };

  const save = async () => {
    const specs = {};
    specText.split("\n").filter(Boolean).forEach((l) => {
      const [k, ...rest] = l.split(":");
      if (k && rest.length) specs[k.trim()] = rest.join(":").trim();
    });
    const body = { ...form, specs, images: (form.images || []).filter(Boolean), price: form.price_on_request ? null : Number(form.price) || 0 };
    try {
      if (edit) await api.put(`/${resource}/${edit.id}`, body);
      else await api.post(`/${resource}`, body);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/${resource}/${id}`);
    toast.success("Deleted");
    load();
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/${resource}/${id}/duplicate`);
      toast.success("Duplicated");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>{isRefurb ? "Refurbished Stock" : "Products"}</h1>
          <p className="mt-2 text-sm text-slate-500">{items.length} items</p>
        </div>
        <div className="flex gap-2">
          <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={(e) => doCsvImport(e.target.files?.[0])} data-testid="csv-input" />
          <Button variant="outline" onClick={() => csvRef.current?.click()} disabled={importing} data-testid="csv-import-btn">
            <UploadIcon className="mr-1 h-4 w-4" /> {importing ? "Importing…" : "Import CSV"}
          </Button>
          <Button onClick={openNew} data-testid="add-item-btn" className="bg-[#0F2942] text-white hover:bg-[#0A192F]"><Plus className="mr-1 h-4 w-4" /> Add</Button>
        </div>
      </div>
      <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              {isRefurb && <TableHead>Condition</TableHead>}
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`row-${p.id}`}>
                <TableCell className="font-medium text-[#0F2942]">{p.name}</TableCell>
                <TableCell className="text-slate-600">{p.category}</TableCell>
                <TableCell>{p.price_on_request ? "On Request" : `₹ ${p.price?.toLocaleString("en-IN")}`}</TableCell>
                {isRefurb && <TableCell>{p.condition} · {p.warranty_months}m</TableCell>}
                <TableCell>{p.is_active ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => duplicate(p.id)} data-testid={`dup-${p.id}`} title="Duplicate"><Copy className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`del-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={isRefurb ? 6 : 5} className="py-10 text-center text-slate-500">No items yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle className="text-xl font-bold text-[#0F2942]">{edit ? "Edit" : "New"} {isRefurb ? "Refurbished Item" : "Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            </div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <Label className="mb-2 block">Product Image</Label>
              <FileUploader kind="product" accept="image/*" value={form.images?.[0] || ""} onChange={(u) => setForm({ ...form, images: [u] })} label="Upload image" />
              <Input className="mt-2" placeholder="…or paste image URL" value={form.images?.[0] || ""} onChange={(e) => setForm({ ...form, images: [e.target.value] })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>HSN</Label><Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} /></div>
              <div><Label>GST %</Label><Input type="number" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })} /></div>
              <div><Label>Price (₹)</Label><Input type="number" disabled={form.price_on_request} value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch id="por" checked={form.price_on_request} onCheckedChange={(v) => setForm({ ...form, price_on_request: v })} /><Label htmlFor="por">Price on Request</Label></div>
            {isRefurb && (
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Condition</Label><Input value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} /></div>
                <div><Label>Warranty (months)</Label><Input type="number" value={form.warranty_months} onChange={(e) => setForm({ ...form, warranty_months: Number(e.target.value) })} /></div>
                <div className="flex items-end gap-2"><Switch id="avl" checked={form.availability} onCheckedChange={(v) => setForm({ ...form, availability: v })} /><Label htmlFor="avl">Available</Label></div>
              </div>
            )}
            <div><Label>Specs (one per line, "key: value")</Label><Textarea rows={4} value={specText} onChange={(e) => setSpecText(e.target.value)} placeholder="Voltage: 220V&#10;Warranty: 2 Years" /></div>
            <div>
              <Label className="mb-2 block">Brochure (PDF)</Label>
              <FileUploader kind="brochure" accept="application/pdf" value={form.brochure_url || ""} onChange={(u) => setForm({ ...form, brochure_url: u })} label="Upload PDF" />
              <Input className="mt-2" placeholder="…or paste brochure URL" value={form.brochure_url || ""} onChange={(e) => setForm({ ...form, brochure_url: e.target.value })} />
            </div>
            <div className="flex items-center gap-2"><Switch id="act" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label htmlFor="act">Active</Label></div>
            <Button onClick={save} data-testid="save-item-btn" className="w-full bg-[#0F2942] text-white hover:bg-[#0A192F]">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
